import { useMemo } from "react";

import { useAsyncQuery } from "@/ds";
import type { FilterDefinition } from "@/ds";
import {
  EMBARCACAO_FILTER_COLUMNS,
  EMBARCACAO_FILTER_LABELS,
  listEmbarcacaoFilterOptions,
} from "@/modules/embarcacoes";
import type { EmbarcacaoFilterColumn, EmbarcacaoFilterOption } from "@/modules/embarcacoes";
import { EMBARCACOES_ERROR_MESSAGE, EMBARCACOES_FORBIDDEN_MESSAGE } from "../embarcacoes.constants";

const NO_OPTIONS: EmbarcacaoFilterOption[] = [];

/** Opções de uma coluna, lidas uma vez do serviço da própria coluna (e não da página em tela). */
function useOpcoesDaColuna(column: EmbarcacaoFilterColumn) {
  return useAsyncQuery({
    fetcher: (signal) => listEmbarcacaoFilterOptions({ column, signal }),
    queryKey: column,
    initialData: NO_OPTIONS,
    forbiddenMessage: EMBARCACOES_FORBIDDEN_MESSAGE,
    errorMessage: EMBARCACOES_ERROR_MESSAGE,
  });
}

/**
 * Colunas filtráveis do painel, cada uma ligada ao serviço de opções da sua coluna. Uma consulta por coluna mantém o
 * carregamento independente: uma lista que falha não derruba as demais. Cada coluna aceita vários valores.
 */
export function useEmbarcacoesFiltrosColunas(): FilterDefinition[] {
  const acronimo = useOpcoesDaColuna("acronimo");
  const embarcacao = useOpcoesDaColuna("embarcacao");
  const tipo = useOpcoesDaColuna("tipo");
  const situacao = useOpcoesDaColuna("situacao");

  return useMemo(() => {
    const states = { acronimo, embarcacao, tipo, situacao };

    return EMBARCACAO_FILTER_COLUMNS.map((key) => {
      const label = EMBARCACAO_FILTER_LABELS[key];
      const { status, data } = states[key];

      return {
        key,
        label,
        options: data,
        isLoading: status === "loading",
        hasError: status === "error" || status === "forbidden",
        type: "multiselect" as const,
        searchPlaceholder: `Buscar ${label.toLowerCase()}`,
      };
    });
  }, [acronimo, embarcacao, tipo, situacao]);
}
